import axios, { type AxiosRequestConfig } from "axios";
import urlJoin from "url-join";
import { fetchAuthSession } from "aws-amplify/auth";
import { getImpersonationDomain } from "../features/impersonation/storage";

interface ApiRequestOptions {
  path: string;
  method: string;
  body?: any;
  onSuccess?: (data: any) => void;
  onFailure?: (error: any) => void;
  onFinally?: () => void;
}

const getAccessTokenPromise = async (): Promise<string | null> => {
  try {
    const { tokens } = await fetchAuthSession();
    return tokens?.accessToken?.toString() || null;
  } catch (error) {
    console.error("Error fetching access token:", error);
    return null;
  }
};

export const makeApiRequestPromise = async ({
  path,
  method,
  body,
  params,
  baseUrl
}: Pick<ApiRequestOptions, "path" | "method" | "body"> & {
  params?: Record<string, string | number | string[] | undefined>;
  baseUrl?: string;
}): Promise<any> => {
  const base = baseUrl || import.meta.env.VITE_EVAL_API_URL;
  const apiUrl = urlJoin(base, path);

  let headers: AxiosRequestConfig["headers"] = {
    "Content-Type": "application/json",
  };

  const apiKey = import.meta.env.VITE_API_KEY;
  if (apiKey) {
    headers["api-key"] = apiKey;
  } else {
    const token = await getAccessTokenPromise();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  }

  // Staff "view as customer" override: scopes reads to the impersonated domain.
  // Honored by the backend only for @composo.ai staff (see get_api_key_or_user),
  // and only for reads — mutating requests are rejected with 403.
  const impersonateDomain = getImpersonationDomain();
  if (impersonateDomain) {
    headers["X-Impersonate-Domain"] = impersonateDomain;
  }

  try {
    const response = await axios({
      method: method,
      url: apiUrl,
      data: method === "GET" ? undefined : JSON.stringify(body),
      params,
      paramsSerializer: (p) => {
        if (!p) return "";
        const search = new URLSearchParams();
        for (const [key, value] of Object.entries(p)) {
          if (value === undefined || value === null) continue;
          if (Array.isArray(value)) {
            for (const item of value) {
              search.append(key, String(item));
            }
          } else {
            search.append(key, String(value));
          }
        }
        return search.toString();
      },
      headers: headers,
    });

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const makeFileUploadRequest = async ({
  path,
  method,
  files,
  baseUrl,
  extraFields,
}: {
  path: string;
  method: string;
  files: File[];
  baseUrl?: string;
  extraFields?: Record<string, string>;
}): Promise<any> => {
  const base = baseUrl || import.meta.env.VITE_EVAL_API_URL;
  const apiUrl = urlJoin(base, path);

  const formData = new FormData();
  files.forEach((f) => formData.append("file", f));
  if (extraFields) {
    Object.entries(extraFields).forEach(([key, value]) =>
      formData.append(key, value)
    );
  }

  const headers: AxiosRequestConfig["headers"] = {};
  const apiKey = import.meta.env.VITE_API_KEY;
  if (apiKey) {
    headers["api-key"] = apiKey;
  } else {
    const token = await getAccessTokenPromise();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  }

  const impersonateDomain = getImpersonationDomain();
  if (impersonateDomain) {
    headers["X-Impersonate-Domain"] = impersonateDomain;
  }

  const response = await axios({
    method,
    url: apiUrl,
    data: formData,
    headers,
  });
  return response.data;
};
